import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { DatabaseRow as CredentialDatabaseRow } from "namirasoft-credential";
import { DatabaseConnectType, DatabaseInputRow, DatabaseRow, DatabaseType, EnvironmentRow, KubernetesRow, ProjectRow, ServerRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InfraEntities } from "../InfraEntities";
import { InfraLayout } from '../InfraLayout';
import { InfraRouterProps } from "../InfraRouterProps";

interface DatabaseEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface DatabaseEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    type?: DatabaseType;
    connect_type?: DatabaseConnectType;
}

export default class DatabaseEditPage extends Component<DatabaseEditPageProps, DatabaseEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxEnum_DatabaseType = createRef<NSBoxEnum<DatabaseType>>();
    NSBoxEntity_DatabaseCredentialID = createRef<NSBoxEntity<CredentialDatabaseRow>>();
    NSBoxEnum_ConnectType = createRef<NSBoxEnum<DatabaseConnectType>>();
    NSBoxEntity_ConnectServerID = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_ConnectKubernetesID = createRef<NSBoxEntity<KubernetesRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: DatabaseEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<DatabaseRow, DatabaseInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.database(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): DatabaseInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let type = this.NSBoxEnum_DatabaseType.current?.getValueEnumOne(DatabaseType, DatabaseType.Clickhouse) ?? DatabaseType.Clickhouse;
                                        let database_credential_id = this.NSBoxEntity_DatabaseCredentialID.current?.getValueOne() ?? "";
                                        let connect_type = this.NSBoxEnum_ConnectType.current?.getValueEnumOne(DatabaseConnectType, DatabaseConnectType.Direct) ?? DatabaseConnectType.Direct;
                                        let connect_server_id = this.NSBoxEntity_ConnectServerID.current?.getValueOne(this.state.connect_type === DatabaseConnectType.ByServer) ?? null;
                                        let connect_kubernetes_id = this.NSBoxEntity_ConnectKubernetesID.current?.getValueOne(this.state.connect_type === DatabaseConnectType.ByKubernetes) ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            type,
                                            database_credential_id,
                                            connect_type,
                                            connect_server_id,
                                            connect_kubernetes_id,
                                            description,
                                            database_category: [],
                                            database_field: [],
                                            database_tag: [],
                                        };
                                    },
                                    setRow: (row: DatabaseRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxEnum_DatabaseType.current?.setValueOne(row.type);
                                        this.NSBoxEnum_ConnectType.current?.setValueOne(row.connect_type);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                type: row.type,
                                                connect_type: row.connect_type
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_DatabaseCredentialID.current?.setValueOne(row.database_credential_id);
                                                this.NSBoxEntity_ConnectServerID.current?.setValueOne(row.connect_server_id);
                                                this.NSBoxEntity_ConnectKubernetesID.current?.setValueOne(row.connect_kubernetes_id);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Type",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<DatabaseType>
                                                        ref={this.NSBoxEnum_DatabaseType}
                                                        title="Database Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => DatabaseType}
                                                        onChanged={(box) =>
                                                        {
                                                            this.setState(
                                                                () => ({ type: box.getValueOne() as DatabaseType }),
                                                                this.NSBoxEntity_DatabaseCredentialID.current?.reload,
                                                            );
                                                        }}
                                                        // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-database-type" }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.type &&
                                                        <NSBoxEntity<CredentialDatabaseRow>
                                                            ref={this.NSBoxEntity_DatabaseCredentialID}
                                                            title={`${NamingConvention.auto.convert(this.state.type, NamingConvention.Pascal_Case_Space)} Credential`}
                                                            required
                                                            multiple={false}
                                                            table_name={this.state.type.toLowerCase()}
                                                            list={this.props.servers.credential.database.List}
                                                            getValue={(item) => item.id}
                                                            getTitle={(item) => item.name}
                                                            getSort={() => null}
                                                            // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-connect-database-credential" }}
                                                            style={{ width: NSBox.width.double }}
                                                        />
                                                    }
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "Connect",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEnum<DatabaseConnectType>
                                                        ref={this.NSBoxEnum_ConnectType}
                                                        title="Connect Type"
                                                        required
                                                        multiple={false}
                                                        getEnumObject={() => DatabaseConnectType}
                                                        getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                        onChanged={(box) => this.setState(() => ({ connect_type: box.getValueOne() as DatabaseConnectType }))}
                                                        // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-connect-database-connect-type" }}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {
                                                        this.state.connect_type === DatabaseConnectType.Direct &&
                                                        <img
                                                            style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                            src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/direct.png"
                                                            alt="Direct"
                                                        />
                                                    }
                                                    {
                                                        this.state.connect_type === DatabaseConnectType.ByServer &&
                                                        <>
                                                            <NSBoxEntity<ServerRow>
                                                                ref={this.NSBoxEntity_ConnectServerID}
                                                                title="Connect Server"
                                                                required
                                                                multiple={false}
                                                                table_name="server"
                                                                list={async (filters, page, size, sorts) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.server,
                                                                            namirasoft.infra.tables.server.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-connect-server" }}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/by-server.png"
                                                                alt="By Server"
                                                            />
                                                        </>
                                                    }
                                                    {
                                                        this.state.connect_type === DatabaseConnectType.ByKubernetes &&
                                                        <>
                                                            <NSBoxEntity<KubernetesRow>
                                                                ref={this.NSBoxEntity_ConnectKubernetesID}
                                                                title="Connect Kubernetes"
                                                                required
                                                                multiple={false}
                                                                table_name="kubernetes"
                                                                list={async (filters, page, size, sort, all_workspaces) =>
                                                                {
                                                                    if (!filters)
                                                                        filters = [];

                                                                    filters.push(new FilterItem(
                                                                        namirasoft.infra.tables.kubernetes,
                                                                        namirasoft.infra.tables.kubernetes.columns.agent_pod_installation_enabled,
                                                                        false,
                                                                        FilterItemOperator.all.equals,
                                                                        "1",
                                                                    ));

                                                                    if (this.state.selected_environment_id)
                                                                        filters.push(new FilterItem(
                                                                            namirasoft.infra.tables.kubernetes,
                                                                            namirasoft.infra.tables.kubernetes.columns.environment_id,
                                                                            false,
                                                                            FilterItemOperator.all.equals,
                                                                            this.state.selected_environment_id,
                                                                        ));

                                                                    return await this.props.servers.infra.kubernetes.List(filters, page, size, sort, all_workspaces);
                                                                }}
                                                                getValue={(item) => item.id}
                                                                getTitle={(item) => item.name}
                                                                getSort={() => null}
                                                                // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-connect-kubernetes" }}
                                                                style={{ width: NSBox.width.double }}
                                                            />
                                                            <img
                                                                style={{ width: "100%", maxWidth: NSBox.width.double }}
                                                                src="https://static.namirasoft.com/image/namirasoft/infra/diagram/database/connect-type/by-kubernetes.png"
                                                                alt="By Kubernetes"
                                                            />
                                                        </>
                                                    }
                                                </NSColumn>
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-name" }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_ConnectServerID.current?.setValue(null);
                                    this.NSBoxEntity_ConnectKubernetesID.current?.setValue(null);
                                    this.NSBoxEntity_ConnectServerID.current?.reload();
                                    this.NSBoxEntity_ConnectKubernetesID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-log-group" }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required
                        title="Description"
                        min_length={50}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/database/#field-description" }}
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </InfraLayout>
        );
    }
}